import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Loader2, Megaphone, ArrowUpRight, Users, IndianRupee, Layers, Sheet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

// Lists every bulk campaign assigned to this Campaign Manager.
// Admins see all. TMs are blocked at API level (and route level).
export const BulkCampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/bulk/campaigns`, { withCredentials: true });
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load bulk campaigns');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="bulk-campaigns-list-page">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
              <Megaphone className="text-[#FF451A]" size={32} />
              Bulk Campaigns
            </h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Marketplace campaigns with 50-500 creators · fixed-cost · pipeline tracking</p>
          </div>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="bulk-empty-state">
              <Megaphone className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No bulk campaigns assigned to you yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Wait for the Admin to convert an incoming brief into a bulk campaign and route it to you.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bulk-campaigns-grid">
              {campaigns.map((c) => (
                <Link
                  key={c.id}
                  to={`/bulk/campaigns/${c.id}`}
                  className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:-translate-y-[2px] transition-all flex flex-col gap-3"
                  data-testid="bulk-campaign-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate" data-testid="bulk-campaign-card-name">{c.campaign_name}</div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-0.5">{c.brand_name}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.google_sheet?.client_url && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(c.google_sheet.client_url, '_blank', 'noopener'); }}
                          className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-[#0F9D58]/10 text-[#0F9D58] border border-[#0F9D58]/25 hover:bg-[#0F9D58]/20 inline-flex items-center gap-1 transition-colors"
                          data-testid={`bulk-card-sheet-chip-${c.id}`}
                          title="Open client sheet"
                        >
                          <Sheet size={10} /> Sheet
                        </button>
                      )}
                      <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/20">
                        Bulk
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-1.5 text-zinc-300">
                      <Users size={13} className="text-[#6B6B6B] dark:text-zinc-500" />
                      <span className="font-mono">{c.bulk_creator_count || 0}</span> creators
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-zinc-300">
                      <IndianRupee size={13} className="text-[#00C972]" />
                      <span className="font-mono">{fmtInr(c.fixed_cost_per_creator)}</span> / creator
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#6B6B6B] dark:text-zinc-500">
                    <span className="inline-flex items-center gap-1"><Layers size={11} /> {(c.platforms || []).join(', ') || '—'}</span>
                    <span className="text-[#FF451A] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open workspace <ArrowUpRight size={11} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
